<template>
  <section class="w-full">
    <header class="container mx-auto px-4 mb-8 flex items-center justify-between">
      <h3 class="text-xl sm:text-2xl font-black tracking-wide uppercase">{{ title || tt('titles.latestMaterials','Laatst toegevoegde materialen') }}</h3>
      <span v-if="loading" class="mt-1 block text-sm text-gray-500">{{ tt('messages.loading','Laden…') }}</span>
    </header>

    <div class="container mx-auto px-4">
      <div v-if="error" class="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {{ t('messages.errorFetchingMaterials') || 'Fout bij ophalen materialen' }}: {{ error }}
      </div>

      <div v-else class="relative">
        <!-- Horizontale scroller met vierkante blokken -->
        <div ref="scroller" class="overflow-x-auto scroll-smooth mmlm-scroller" tabindex="0" role="region" :aria-label="t('aria.latestMaterialsRegion') || 'Laatste materialen'" style="scroll-snap-type: x mandatory;">
          <ul class="flex items-stretch gap-5 list-none" style="white-space: nowrap;">
            <li v-for="item in items" :key="item.id" class="shrink-0" style="scroll-snap-align: start;">
              <article class="mm-material-tile mm-material-tile--slider mmlm-card group relative block w-[300px] h-[412px]  border border-black overflow-hidden p-0 m-0">
                <!-- Maak hele tegel aanklikbaar met overlay link -->
                <a :href="materialHref(item)"
                   class="absolute inset-0 z-10"
                   :aria-label="(item.title || item.name || '') + ' – ' + tt('materials.viewMaterial','Bekijk materiaal')"
                   tabindex="0"></a>
                <!-- Afbeelding bovenin -->
                <div class="mmlm-card-imglink relative block h-[58%] w-full overflow-hidden p-0 m-0">
                  <img :src="item.image || placeholder" :alt="item.title || item.name || ''" class="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"/>
                  

                  <!-- Views rechts onder in de foto -->
                  <div class="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20.571" height="16" viewBox="0 0 20.571 16">
                    <path d="M4.611,43.764A13.956,13.956,0,0,1,1.875,40a13.8,13.8,0,0,1,2.736-3.764,8.158,8.158,0,0,1,5.675-2.521,8.158,8.158,0,0,1,5.675,2.521A13.856,13.856,0,0,1,18.7,40a13.8,13.8,0,0,1-2.736,3.764,8.158,8.158,0,0,1-5.675,2.521A8.158,8.158,0,0,1,4.611,43.764ZM10.286,48c5.657,0,9.214-5.332,10.286-8-1.071-2.668-4.629-8-10.286-8S1.071,37.332,0,40C1.071,42.668,4.629,48,10.286,48Zm0-5.143A2.856,2.856,0,0,1,7.429,40a2.9,2.9,0,0,1,.054-.561l2.8.561-.561-2.8a2.9,2.9,0,0,1,.561-.054,2.857,2.857,0,0,1,0,5.714ZM5.714,40a4.571,4.571,0,1,0,4.571-4.571,4.362,4.362,0,0,0-.9.089,4.578,4.578,0,0,0-3.582,3.561l0,.025a4.624,4.624,0,0,0-.089.9Z" transform="translate(0 -32)" fill="#fff"/>
                  </svg>
                  <span class="text-xs font-semibold">{{ item.views ?? 0 }}</span>
                </div>

                  <!-- Diagonaal label voor reeds geclaimde producten -->
                  <div v-if="item.claimed" class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12 z-20 w-[140%] text-center">
                    <span class="inline-block bg-[#E24C3B] text-white px-4 py-1.5 text-sm font-black uppercase tracking-widest shadow-lg select-none">
                      {{ (t('materials.tooLate') || 'Te laat').toUpperCase() }}
                    </span>
                  </div>
              </div>

              <!-- Content onder de foto (volledige breedte van de kaart) -->
              <div class="mmlm-card-body flex h-[42%] w-full flex-col justify-between p-5 m-0">
                <header class="">
                  <h2 class="mm-material-tile-title !text-[18px] !leading-[21px] sm:!text-[20px] sm:!leading-snug !font-semibold text-neutral-900 line-clamp-2">
                    {{ item.title || item.name || '' }}</h2>
                  <p class="mt-1 text-[18px] leading-snug text-neutral-700 clamp-2">{{ item.teaser || '' }}</p>
                </header>

                <div class="mm-material-tile-meta flex items-center justify-between gap-2">
                  <!-- Categorie icoon → klik naar materialenoverzicht met deze categorie actief -->
                  <a
                    v-if="overviewCategoryHref(item)"
                    :href="overviewCategoryHref(item)"
                    class="mm-material-tile-category inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-100 shrink-0 relative z-20"
                    :aria-label="tt('materials.showCategoryItems','Toon materialen in categorie') + ' ' + (item.category?.name || '')"
                    :title="item.category?.name || tt('materials.category','Categorie')"
                    @click.stop
                  >
                    <img v-if="item.category?.imageUrl" :src="item.category.imageUrl" :alt="item.category?.name || tt('materials.category','Categorie')" class="h-full w-full object-cover" loading="lazy" decoding="async"/>
                    <img v-else :src="catPlaceholder" :alt="item.category?.name || tt('materials.category','Categorie')" class="h-8 w-8 object-cover" loading="lazy" decoding="async"/>
                  </a>
                  <span v-else class="mm-material-tile-category inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-100  shrink-0">
                    <img v-if="item.category?.imageUrl" :src="item.category.imageUrl" :alt="item.category?.name || tt('materials.category','Categorie')" class="h-full w-full object-cover" loading="lazy" decoding="async"/>
                    <img v-else :src="catPlaceholder" :alt="item.category?.name || tt('materials.category','Categorie')" class="h-8 w-8 object-cover" loading="lazy" decoding="async"/>
                  </span>

                  <!-- Prijs pill -->
                  <span v-if="String(item.mm_type || item.type || '').toLowerCase() !== 'gevraagd' && item.price != null && Number(item.price) !== 0 && !Number.isNaN(Number(item.price))" class="mm-material-tile-price flex overflow-hidden whitespace-nowrap items-center rounded-full  bg-white text-sm text-black">
                    <span class="shrink-0 px-3 py-1 text-lg leading-6 text-black">€ {{ priceText(item.price) }}</span>
                  </span>

                  <!-- Type chip -->
                  <span v-if="String(item.mm_type || item.type || '').toLowerCase() === 'gevraagd' || String(item.mm_type || item.type || '').toLowerCase() === 'requested'" class="mm-material-tile-type bg-black text-white px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full relative z-20">
                    {{ tt('materials.requested', 'Gevraagd') }}
                  </span>
                  <span v-else class="mm-material-tile-type bg-[#E7D7BF] text-black px-3 py-1 text-[11px] font-black uppercase italic tracking-wider rounded-full relative z-20">
                    {{ tt('materials.offered', 'Aangeboden') }}
                  </span>

                  <!-- Bekijk knop (navigeert ook, maar geen tweede anchor binnen de overlay) -->
                  <button @click="goTo(item)" type="button"
                          :aria-label="tt('materials.viewMaterial','Bekijk materiaal')"
                          class="mm-material-tile-view-details ml-auto z-20 flex h-10 w-10 items-center justify-center rounded-none text-black border border-solid border-black hover:bg-black hover:text-white shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="19.058" height="16" viewBox="0 0 19.058 16"
                         class="transition-colors duration-300">
                      <path id="arrow-right-sharp-regular"
                            d="M18.333,77.127l.725-.725-.725-.725-6.552-6.552-.725-.725L9.61,69.846l.725.725,4.8,4.807H0v2.047H15.138l-4.807,4.8-.725.725L11.052,84.4l.725-.725Z"
                            transform="translate(0 -68.4)" fill="currentColor"/>
                    </svg>




                  </button>
                </div>
              </div>
            </article>
          </li>
        </ul>
      </div>

        <!-- Pijlen onder midden -->
        <div class="mt-4 flex justify-center">
          <div class="inline-flex items-center gap-3">
            <button v-if="canScrollLeft" @click="scrollPrev" class="mmlm-arrow" :aria-label="t('aria.scrollLeft') || 'Scroll links'">


              <svg xmlns="http://www.w3.org/2000/svg" width="19.058" height="16" viewBox="0 0 19.058 16"
                   class="transition-colors duration-300" style="transform: rotate(180deg);">
                <path id="arrow-right-sharp-regular"
                      d="M18.333,77.127l.725-.725-.725-.725-6.552-6.552-.725-.725L9.61,69.846l.725.725,4.8,4.807H0v2.047H15.138l-4.807,4.8-.725.725L11.052,84.4l.725-.725Z"
                      transform="translate(0 -68.4)" fill="#fff"/>
              </svg>




            </button>
            <button v-if="canScrollRight" @click="scrollNext" class="mmlm-arrow" :aria-label="t('aria.scrollRight') || 'Scroll rechts'">

              <svg xmlns="http://www.w3.org/2000/svg" width="19.058" height="16" viewBox="0 0 19.058 16"
                   class="transition-colors duration-300">
                <path id="arrow-right-sharp-regular"
                      d="M18.333,77.127l.725-.725-.725-.725-6.552-6.552-.725-.725L9.61,69.846l.725.725,4.8,4.807H0v2.047H15.138l-4.807,4.8-.725.725L11.052,84.4l.725-.725Z"
                      transform="translate(0 -68.4)" fill="#fff"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import WpApiClient from '../lib/wp-api.js'
import { t as i18nT } from '../i18n'

const t = (key) => i18nT(key)
function tt(key, fallback){
  try {
    const v = i18nT(key)
    if (!v || v === key || /\b[A-Za-z]+\.[A-Za-z]+/.test(v)) return fallback
    return v
  } catch { return fallback }
}

function padId(val){
  const n = Number(val)
  if (!Number.isFinite(n) || n <= 0) return val || ''
  try { return String(Math.trunc(n)).padStart(8, '0') } catch { return String(n) }
}

const props = defineProps({
  apiBase: { type: String, default: '' },
  perPage: { type: Number, default: 24 },
  endpoint: { type: String, default: '/wp-json/wp/v2/materiaal' },
  title: { type: String, default: '' },
})

const api = new WpApiClient({ 
  base: props.apiBase,
  cacheBust: true 
})

const placeholder = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect width="200" height="200" fill="#f3f4f6"/><path d="M0 150 L60 100 L110 140 L150 110 L200 150 L200 200 L0 200 Z" fill="#e5e7eb"/></svg>`)
// Placeholder voor categorie-icoon
const catPlaceholder = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#e5e7eb"/><circle cx="20" cy="20" r="12" fill="#d1d5db"/></svg>`)

const items = ref([])
const loading = ref(true)
const error = ref('')

// Horizontal scroll container
const scroller = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

function updateScrollButtons() {
  const el = scroller.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 0
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 5
}

function scrollByDelta(delta){
  const el = scroller?.value
  if (!el) return
  try { el.scrollBy({ left: delta, behavior: 'smooth' }) }
  catch(e){ el.scrollLeft += delta }
}
function scrollPrev(){
  const el = scroller?.value
  const w = el ? el.clientWidth : 320
  scrollByDelta(-Math.max(240, Math.floor(w * 0.9)))
}
function scrollNext(){
  const el = scroller?.value
  const w = el ? el.clientWidth : 320
  scrollByDelta(Math.max(240, Math.floor(w * 0.9)))
}

async function fetchLatest() {
  loading.value = true
  error.value = ''
  try {
    const fetchLimit = Math.max(props.perPage, 48)
    const { items: raw } = await api.getMaterials({ endpoint: props.endpoint, page: 1, perPage: fetchLimit, sort: 'newest' })
    const all = raw.map(m => normalizeMaterial(m))
    // Slice to the requested perPage
    items.value = all.slice(0, props.perPage)
  } catch (e) {
    error.value = e?.message || String(e)
  } finally {
    loading.value = false
  }
}

function decodeHTML(str) {
  if (!str || typeof str !== 'string' || !str.includes('&')) return str || ''
  try {
    const doc = new DOMParser().parseFromString(str, 'text/html')
    return doc.documentElement.textContent || str
  } catch { return str }
}

function normalizeMaterial(m = {}) {
  // Titel
  const rawTitle = m?.title?.rendered || m?.title || m?.name || ''
  const title = decodeHTML(rawTitle)
  const slug = m?.slug || ''

  // Afbeelding (featured media of alternate velden)
  let image = ''
  try {
    const media = m?._embedded?.['wp:featuredmedia']?.[0]
    image = media?.media_details?.sizes?.large?.source_url || media?.source_url || ''
  } catch {}
  if (!image) image = m?.image_url || m?.image || ''

  // Excerpt / beschrijving (kort)
  // Teaser: haal uit beschrijving/content, niet meer uit samenvatting
  const rawDesc = (m?.description || m?.content?.rendered || m?.content || '').toString()
  const plain = rawDesc.replace(/<[^>]*>/g,'').replace(/\s+/g,' ').trim()
  const teaserRaw = plain.length <= 140 ? plain : (plain.slice(0,140).replace(/\s+\S*$/, '') + '…')
  const teaser = decodeHTML(teaserRaw)

  // Categorie (pak primaire categorie uit eigenschappen of embedded terms)
  let category = null
  if (m?.eigenschappen?.categorie) {
    const ec = m.eigenschappen.categorie
    category = { 
      id: ec.id, 
      slug: ec.slug || '', 
      name: decodeHTML(ec.name || ''), 
      imageUrl: ec.image_url || ec.imageUrl || '' 
    }
  }

  if (!category) {
    try {
      const pc = m?.primary_category || m?.primaryCategory
      if (pc && (pc.name || pc.image_url || pc.imageUrl)) {
        category = { id: pc.id || pc.term_id || null, slug: pc.slug || '', name: decodeHTML(pc.name || ''), imageUrl: pc.image_url || pc.imageUrl || '' }
      }
    } catch {}
  }
  
  if (!category) {
    try {
      const term = m?._embedded?.['wp:term']?.flat()?.find(t => t && t.taxonomy === 'materiaal_categorie')
      if (term) {
        let img = term.image_url || term.image || term.thumbnail || ''
        if (!img && term.acf) {
          const candidates = ['afbeelding', 'image', 'thumbnail', 'term_image', 'icon']
          for (const key of candidates) {
            const val = term.acf[key]
            if (val) {
              if (typeof val === 'string') { img = val; break }
              if (val.sizes?.medium) { img = val.sizes.medium; break }
              if (val.sizes?.thumbnail) { img = val.sizes.thumbnail; break }
              if (val.url) { img = val.url; break }
            }
          }
        }
        category = { id: term.id || term.term_id || null, slug: term.slug || '', name: decodeHTML(term.name || ''), imageUrl: img || '' }
      }
    } catch {}
  }

  // Prijs
  const price = m?.eigenschappen?.waarde_eur ?? m?.price ?? m?.meta?.mm_waarde_eur ?? m?.mm_waarde_eur ?? m?.waarde ?? null
  const materialType = m?.mm_type || m?.type || m?.meta?.mm_type || 'aangeboden'

  // Provider
  const author = m?._embedded?.author?.[0] || null
  const authorRole = m?.mm_author_role || author?.mm_role_type || author?.role || m?.author_role || ''
  const providerName = author?.mm_org_name || author?.name || m?.author_name || author?.slug || null
  const providerWebsite = author?.url || author?.website || m?.author_website || null
  const providerLogo = author?.mm_company_logo_url || m?.author_company_logo_url || null
  const provider = (providerName || providerWebsite || providerLogo || authorRole) ? {
    name: providerName,
    website: providerWebsite,
    avatarUrl: (author?.avatar_urls && (author.avatar_urls['96'] || author.avatar_urls['48'] || author.avatar_urls['24'])) || null,
    companyLogoUrl: providerLogo,
    role: authorRole,
  } : null

  // Views
  const views = m?.mm_views ?? m?.views ?? m?.meta?.mm_views ?? 0

  // Claimed status
  const claim = m?.mm_claim || null
  const statusKey = String(m?.statusKey || claim?.status || '').toLowerCase()
  const hasActiveClaim = !!(claim && Number(claim.claimed_by_user || 0) !== 0 && String(claim.status || '').toLowerCase() !== 'cancelled')
  const claimed = (typeof m?.claimed !== 'undefined') ? !!m.claimed : (statusKey === 'geclaimd' || hasActiveClaim)

  return { id: m.id, title, slug, image, teaser, category, price, views, claimed, provider, mm_author_role: authorRole, mm_type: materialType, type: materialType }
}

function materialHref(m){
  if (!m) return '#'
  if (m.slug && m.id) return `/materiaal/${m.slug}-${m.id}`
  if (m.slug) return `/materiaal/${m.slug}`
  return '#'
}

function goTo(m){
  const href = materialHref(m)
  if (!href || href === '#') return
  try { window.location.assign(href) }
  catch { window.location.href = href }
}

// Price formatting helper used in the template
function priceText(p){
  const n = Number(p)
  if (Number.isNaN(n)) return '—'
  try { return n.toFixed(0) } catch { return String(n) }
}

// Bepaal basispad voor materialenoverzicht, gelijk aan MmMaterialsOverview
function getMaterialsBasePath(){
  try {
    const path = window.location.pathname.replace(/\/+$/, '') || '/'
    const parts = path.split('/').filter(Boolean)
    const idx = parts.indexOf('materialen')
    if (idx !== -1) return '/' + parts.slice(0, idx + 1).join('/')
  } catch (_) {}
  return '/materialen'
}

// Bouw link naar overzicht met geselecteerde categorie actief
function overviewCategoryHref(m){
  try {
    const cat = m?.category || null
    if (!cat || (!cat.slug && !cat.id)) return null
    const base = getMaterialsBasePath()
    if (cat.slug) return `${base}/${cat.slug}`
    if (cat.id) return `${base}?cat=${encodeURIComponent(String(cat.id))}`
    return null
  } catch { return null }
}

onMounted(() => {
  fetchLatest().then(() => updateScrollButtons())
  const el = scroller.value
  if (el) el.addEventListener('scroll', updateScrollButtons)
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Scroller: verberg eventuele standaard scrollbars en bullets */
.mmlm-scroller ul,
.mmlm-scroller li { list-style: none !important; }
.mmlm-scroller ul { padding-left: 0 !important; margin-left: 0 !important; }
.mmlm-scroller li::marker { content: '' !important; }
.mmlm-scroller li::before { content: none !important; display: none !important; }
.mmlm-scroller{ -ms-overflow-style: none; scrollbar-width: none; }
.mmlm-scroller::-webkit-scrollbar { width: 0; height: 0; background: transparent; }

/* Pijlknoppen: vierkante zwarte blokjes met wit pictogram/tekst */
.mmlm-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #000;
  color: #fff;
  border: 1px solid #000;
}
.mmlm-arrow:hover { background:#111; border-color:#111; }

/* Tekst truncation */
.clamp-2{
  display:-webkit-box;
  -webkit-line-clamp:2;
  -webkit-box-orient:vertical;
  overflow:hidden;
}

/* Forceer rand-tot-rand inhoud binnen kaart (voorkomt subkader-effect door thema-css) */
.mmlm-card { padding: 0 !important; margin: 0 !important; }
.mmlm-card > a,
.mmlm-card > div:not(.mmlm-card-body) { padding: 0 !important; margin: 0 !important; width: 100% !important; }
.mmlm-card-imglink img { display: block; width: 100%; height: 100%; object-fit: cover; }

/* Verwijder eventuele standaard list paddings die randen kunnen laten inzakken */
.mmlm-scroller ul { padding-left: 0 !important; padding-right: 0 !important; }
.mmlm-scroller li { margin: 0 !important; }
</style>
